import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, fmtQty } from './InventoryShared';

export default function InventoryCounts() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/inventory/counts?per_page=50`;
      if (status) url += `&status=${status}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status]);

  const fetchAux = useCallback(async () => {
    const res = await fetch(`${API}/companies/${companyId}/inventory/warehouses`, { headers: getAuthHeaders() });
    setWarehouses(await res.json());
  }, [companyId]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAux(); }, [fetchAux]);

  const openView = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/inventory/counts/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🧮 Stock Counts</h2>
        <button style={btnPrimary} onClick={() => setCreating(true)}>+ New Count</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 200 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Count #', 'Warehouse', 'Scope', 'Lines', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No counts</td></tr>}
              {list.map(c => (
                <tr key={c.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    <a href="#v" onClick={e => { e.preventDefault(); openView(c.id); }} style={{ color: '#3b82f6' }}>{c.count_number}</a>
                  </td>
                  <td style={tdStyle}>{c.warehouse?.name || '—'}</td>
                  <td style={tdStyle}>{c.scope_type}</td>
                  <td style={tdStyle}>{c.lines_count ?? 0}</td>
                  <td style={tdStyle}><Badge value={c.status} /></td>
                  <td style={tdStyle}><button style={btnSmall} onClick={() => openView(c.id)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <CreateCountModal companyId={companyId} warehouses={warehouses}
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); fetchList(); }} />
      )}
      {viewing && (
        <CountDetail companyId={companyId} count={viewing}
          onClose={() => { setViewing(null); fetchList(); }}
          onRefresh={() => openView(viewing.id)} />
      )}
    </div>
  );
}

function CreateCountModal({ companyId, warehouses, onClose, onSaved }) {
  const [form, setForm] = useState({ scope_type: 'warehouse', warehouse_id: '', blind_count: false, notes: '' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/counts`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    onSaved();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Stock Count</h3>
        <FormRow label="Scope">
          <select style={inputStyle} value={form.scope_type} onChange={e => setForm({ ...form, scope_type: e.target.value })}>
            <option value="full">Full</option>
            <option value="warehouse">Warehouse</option>
            <option value="cycle">Cycle</option>
          </select>
        </FormRow>
        <FormRow label="Warehouse">
          <select style={inputStyle} value={form.warehouse_id} onChange={e => setForm({ ...form, warehouse_id: e.target.value })}>
            <option value="">— All —</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </FormRow>
        <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
          <input type="checkbox" checked={form.blind_count} onChange={e => setForm({ ...form, blind_count: e.target.checked })} /> Blind Count (hide system qty from counter)
        </label>
        <FormRow label="Notes"><textarea style={{ ...inputStyle, minHeight: 50 }} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></FormRow>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

function CountDetail({ companyId, count, onClose, onRefresh }) {
  const [lines, setLines] = useState(count.lines || []);
  const [busy, setBusy] = useState(false);

  const updateLine = async (lineId, countedQty) => {
    await fetch(`${API}/companies/${companyId}/inventory/counts/${count.id}/lines/${lineId}`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ counted_qty: countedQty }),
    });
    setLines(ls => ls.map(l => l.id === lineId ? { ...l, counted_qty: countedQty, variance_qty: countedQty - l.system_qty } : l));
  };

  const start = async () => {
    setBusy(true);
    await fetch(`${API}/companies/${companyId}/inventory/counts/${count.id}/start`, { method: 'POST', headers: getAuthHeaders() });
    setBusy(false); onRefresh();
  };

  const complete = async () => {
    if (!window.confirm('Complete this count? Counted quantities will be locked.')) return;
    setBusy(true);
    await fetch(`${API}/companies/${companyId}/inventory/counts/${count.id}/complete`, { method: 'POST', headers: getAuthHeaders() });
    setBusy(false); onRefresh();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 720 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ marginTop: 0 }}>{count.count_number} <Badge value={count.status} /></h3>
        </div>
        <table style={tableStyle}>
          <thead><tr>{['Product', 'Location', 'System Qty', 'Counted Qty', 'Variance'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {lines.map(l => (
              <tr key={l.id}>
                <td style={tdStyle}>{l.product?.name || l.product_id}</td>
                <td style={tdStyle}>{l.location?.code || '—'}</td>
                <td style={tdStyle}>{count.blind_count && count.status !== 'completed' ? '—' : fmtQty(l.system_qty)}</td>
                <td style={tdStyle}>
                  {count.status === 'in_progress' ? (
                    <input type="number" style={{ ...inputStyle, width: 90 }} defaultValue={l.counted_qty ?? ''}
                      onBlur={e => e.target.value !== '' && updateLine(l.id, Number(e.target.value))} />
                  ) : (l.counted_qty ?? '—')}
                </td>
                <td style={tdStyle}>{l.counted_qty !== null && l.counted_qty !== undefined ? fmtQty(l.variance_qty) : '—'}</td>
              </tr>
            ))}
            {lines.length === 0 && <tr><td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No lines in scope</td></tr>}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          {count.status === 'draft' && <button style={btnPrimary} onClick={start} disabled={busy}>Start Count</button>}
          {count.status === 'in_progress' && <button style={btnPrimary} onClick={complete} disabled={busy}>Complete Count</button>}
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
